import { Button } from "@mui/material";
import { useState } from "react";
import { FloorWizardUploadLayoutStep } from "src/pages/PlannerPage/FloorWizard/FloorWizardUploadLayoutStep";
import { FloorWizardTutorialStep } from "src/pages/PlannerPage/FloorWizard/FloorWizardTutorialStep";
import { ts } from "src/utils/translate";
import { GetProductsOfRoomsByFloorsFloor } from "src/model/api/v2/data-contracts";
import {
    FloorFormData,
    FloorLayout,
    isFloorUploadStepValid,
    useUpsertFloor
} from "src/pages/PlannerPage/FloorWizard/useUpsertFloor";
import { StepConfig, StepDialog } from "src/pages/PlannerPage/FloorWizard/StepDialog";

interface EditFloorDialogProps {
    projectId: number;
    floor: GetProductsOfRoomsByFloorsFloor;
    onClose: () => void;
}

type Step = 0 | 1;

export const EditFloorDialog = ({ projectId, floor, onClose }: EditFloorDialogProps) => {
    const { upsertFloor, startScaleSetup } = useUpsertFloor(projectId, onClose);

    const [step, setStep] = useState<Step>(0);
    const [formData, setFormData] = useState<FloorFormData>({
        selectedLayout: FloorLayout.UPLOAD,
        floorId: floor.floor_id,
        floorName: floor.floor_name ?? '',
        ceilingHeight: '270',
        dontShowAgain: false,
        planImageFile: null,
        planPreviewImageUrl: null,
        planPdfFile: null,
        planPdfPage: 1,
    });

    const handleSetReferenceLength = async () => {
        const floorId = await upsertFloor(formData);
        if (floorId != null) {
            setFormData((prev) => ({ ...prev, floorId, planImageFile: null }));
            setStep(1);
        }
    };

    const getStepConfig = (): StepConfig => {
        switch (step) {
            case 0:
                return {
                    title: ts('planner_edit_floor', 'Edit floor'),
                    subtitle: ts('upload_layout', 'Upload layout'),
                    maxWidth: 'sm',
                    actionsLeft: (
                        <Button variant="outlined" onClick={onClose} sx={{ width: 120 }}>
                            {ts('planner_deletion_dialog_cancel_button_text', 'Cancel')}
                        </Button>
                    ),
                    actions: (
                        <Button variant="contained" onClick={handleSetReferenceLength} disabled={!isFloorUploadStepValid(formData)} sx={{ width: 180 }}>
                            {ts('set_reference_length', 'Set Reference Length')}
                        </Button>
                    ),
                    body: <FloorWizardUploadLayoutStep projectId={projectId} floorId={floor.floor_id} formData={formData} setFormData={setFormData} />,
                };
            case 1:
                return {
                    title: ts('you_can_easily_set_reference_length', 'You can easily set reference length'),
                    maxWidth: 'sm',
                    actions: (
                        <Button variant="contained" sx={{ width: 120 }} onClick={() => startScaleSetup(Number(formData.floorId))}>
                            {ts('set_reference_length_begin', 'Start')}
                        </Button>
                    ),
                    body: <FloorWizardTutorialStep formData={formData} setFormData={setFormData} />,
                };
        }
    };

    return <StepDialog config={getStepConfig()} onClose={onClose} />;
};